import apiClient from '../middleware/apiClient';

import { ITG_RECOMMENDATIONS, COTTON_ITG_RECOMMENDATIONS } from '../helpers/apis';
import cottonFlagManager from '../helpers/featureFlags/cotton';
import { callApiWithFallback } from '../helpers/apiFallback';
import { kibanaLog } from '../helpers/logger';

// Port of legacy CryptoHelpers.hashAccountNumber + ItgService poll config defaults
export const ITG_POLL_TIMES = [5];
export const ITG_MAX_HITS = 10;
export const ITG_STARTED_KEY = 'itgservice-started';
// 15-minute in-flight window (legacy: 9e5 ms)
export const ITG_INFLIGHT_WINDOW_MS = 9e5;

// Fetch ITG recommendations
export const GET_ITG_RECOMMENDATIONS = 'GET_ITG_RECOMMENDATIONS';
export const GET_ITG_RECOMMENDATIONS_SUCCESS = 'GET_ITG_RECOMMENDATIONS_SUCCESS';
export const GET_ITG_RECOMMENDATIONS_FAILURE = 'GET_ITG_RECOMMENDATIONS_FAILURE';

function dispatchGetItgRecommendationsSuccess(itgRecommendations) {
  return {
    type: GET_ITG_RECOMMENDATIONS_SUCCESS,
    payload: itgRecommendations,
  };
}

function dispatchGetItgRecommendationsFailure(error) {
  return {
    type: GET_ITG_RECOMMENDATIONS_FAILURE,
    payload: error,
  };
}

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const sleep = seconds => delay(seconds * 1000);

// True when an ITG scan was started within the legacy 15-minute window.
export const isItgScanRecent = () => {
  const startedAt = parseInt(localStorage.getItem(ITG_STARTED_KEY), 10);
  return !Number.isNaN(startedAt)
    && (Date.now() - startedAt) < ITG_INFLIGHT_WINDOW_MS;
};

const pollUntilSuccess = async (fetchRecommendations, pollCount = 0) => {
  const response = await fetchRecommendations();

  if (response && response.status === 'success') {
    return response;
  }

  if (pollCount + 1 >= ITG_MAX_HITS) {
    throw new Error('ITG Recommendations request timed out');
  }

  const timeIndex = Math.min(ITG_POLL_TIMES.length - 1, pollCount);
  await sleep(ITG_POLL_TIMES[timeIndex]);
  return pollUntilSuccess(fetchRecommendations, pollCount + 1);
};

export const getItgRecommendations = (trigger = false) => async (dispatch, getState) => {
  const { itgRecommendations: { requested, loading, data } = {} } = getState();
  const isPolling = requested && loading;

  if (data || isPolling) return Promise.resolve();

  const fetchedRecently = isItgScanRecent();

  // Passive cache-check skips the network unless a scan was started recently.
  // A triggered load (component mount / user click) always fetches, mirroring
  // the legacy ItgService constructor that polls on every page load.
  if (!trigger && fetchedRecently) return Promise.resolve();

  if (!fetchedRecently) {
    localStorage.removeItem(ITG_STARTED_KEY);
  }

  const { isAptV1Enabled, itgRecommendationsEnabled } = cottonFlagManager;

  const fallbackTransportDispatch = action => action;
  const fetchRecommendations = () => {
    if (isAptV1Enabled) {
      // Use APT V1 API (100% Kotlin)
      return apiClient.fetch(COTTON_ITG_RECOMMENDATIONS);
    }
    if (itgRecommendationsEnabled) {
      // Try Cotton API, fallback to Ruby API
      return callApiWithFallback(
        fallbackTransportDispatch,
        () => ({ payload: apiClient.fetch(COTTON_ITG_RECOMMENDATIONS) }),
        () => ({ payload: apiClient.fetch(ITG_RECOMMENDATIONS) }),
        'itg_recommendations',
      );
    }

    // Only call Ruby API
    return apiClient.fetch(ITG_RECOMMENDATIONS);
  };

  // Log once per request rather than once per poll attempt.
  if (isAptV1Enabled) {
    kibanaLog('apt_v1_itg_recommendations_api_called');
  }

  dispatch({ type: GET_ITG_RECOMMENDATIONS });

  try {
    const response = await pollUntilSuccess(fetchRecommendations);
    localStorage.setItem(ITG_STARTED_KEY, Date.now());
    return dispatch(dispatchGetItgRecommendationsSuccess(response));
  } catch (fetchError) {
    localStorage.removeItem(ITG_STARTED_KEY);
    return dispatch(dispatchGetItgRecommendationsFailure(fetchError));
  }
};
